/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @link      https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license   MIT
 * @description Frontend notification system with a legacy log and a new ticker-tape banner.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { playSound } from "../utils/sounds";

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  // Initialize notifications from localStorage to persist through refreshes
  const [notifications, setNotifications] = useState(() => {
    const saved = localStorage.getItem("trap_tycoon_notifications");
    if (!saved) return [];
    try {
      const parsed = JSON.parse(saved);
      // Re-map objects to ensure timestamps are actual Date objects
      return parsed.map((n) => ({
        ...n,
        timestamp: new Date(n.timestamp),
      }));
    } catch (e) {
      return [];
    }
  });

  const [tickerQueue, setTickerQueue] = useState([]);
  const [activeTicker, setActiveTicker] = useState(null);
  const [isExiting, setIsExiting] = useState(false);
  const activeMessageRef = useRef(null);

  useEffect(() => {
    activeMessageRef.current = activeTicker?.message;
  }, [activeTicker]);

  // Effect to save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(
      "trap_tycoon_notifications",
      JSON.stringify(notifications),
    );
  }, [notifications]);

  const tickerTapeBanner = useCallback(
    (
      message,
      icon = null,
      repeated = false,
      repeatInterval = 5000,
      repeatAfterAnotherEvent = true,
    ) => {
      setTickerQueue((prev) => {
        // Check if the message is already in the pending queue
        const inQueue = prev.some((item) => item.message === message);
        // Check if the message is currently active on the screen
        const isActive = activeMessageRef.current === message;

        if (inQueue || isActive) {
          return prev; // Discard the duplicate message
        }

        const tickerItem = {
          id: Date.now() + Math.random(),
          message,
          icon,
          repeated,
          repeatInterval,
          repeatAfterAnotherEvent,
        };

        // Append the new message and only keep the most recent 10 entries
        const updatedQueue = [...prev, tickerItem];
        return updatedQueue.slice(-10);
      });
    },
    [],
  );

  const addNotification = useCallback(
    (message, type = "info", sound = "") => {
      const id = Date.now();
      playSound(sound !== "" ? sound : type);
      tickerTapeBanner(message);
      setNotifications((prev) =>
        [
          { id, message, type, timestamp: new Date(), read: false },
          ...prev,
        ].slice(0, 50),
      );
    },
    [tickerTapeBanner],
  );

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    localStorage.removeItem("trap_tycoon_notifications");
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  // Process Ticker Queue
  useEffect(() => {
    if (!activeTicker && tickerQueue.length > 0) {
      const next = tickerQueue[0];
      setTickerQueue((prev) => prev.slice(1));
      setActiveTicker(next);
      setIsExiting(false);
    }
  }, [activeTicker, tickerQueue]);

  const handleTickerComplete = () => {
    if (!activeTicker) return;

    if (activeTicker.repeated) {
      const repeatedItem = { ...activeTicker, id: Date.now() + Math.random() };
      setTimeout(() => {
        setTickerQueue((prev) => [...prev, repeatedItem]);
      }, activeTicker.repeatInterval);
    }

    setIsExiting(true);
    setTimeout(() => {
      setActiveTicker(null);
    }, 500);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        tickerTapeBanner,
        clearNotifications,
        markAllRead,
      }}
    >
      {children}

      {activeTicker && (
        <div
          className={`fixed top-0 left-0 w-full z-[10000] transform transition-transform duration-500 ease-in-out border-b border-green-500/30 bg-black/95 backdrop-blur-sm h-7 flex items-center overflow-hidden font-mono shadow-[0_4px_10px_rgba(0,0,0,0.8)]
            ${isExiting ? "-translate-y-full" : "translate-y-0"}`}
        >
          <div className="bg-green-600 text-black px-3 h-full flex items-center font-black text-[10px] italic tracking-tighter z-20 whitespace-nowrap shadow-[4px_0_10px_rgba(0,0,0,0.5)]">
            {activeTicker.icon || "SYSTEM"} »
          </div>

          <div className="relative flex-1 h-full flex items-center overflow-hidden">
            <div
              onAnimationEnd={handleTickerComplete}
              className="animate-ticker text-green-400 text-[11px] font-bold uppercase whitespace-nowrap will-change-transform"
              style={{
                animationDuration: `${Math.max(
                  activeTicker.message.length * 0.2,
                  10,
                )}s`,
              }}
            >
              {activeTicker.message}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes ticker-scroll {
          0% { transform: translateX(100vw); }
          100% { transform: translateX(-100%); }
        }
        .animate-ticker {
          display: inline-block;
          padding-left: 20px;
          animation-name: ticker-scroll;
          animation-timing-function: linear;
          animation-iteration-count: 1;
        }
      `}</style>
    </NotificationContext.Provider>
  );
};

export const useNotify = () => useContext(NotificationContext);
